import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { TerminalService } from '@app/services/terminal.service';
import { Analytics, setUserProperties } from '@angular/fire/analytics';
import packageJson from '../../../../package.json';
import { UserService, UserEx } from '@app/services/user.service';
import { AuthService } from '@app/services/auth.service';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { Firestore } from '@angular/fire/firestore';
import { Utility } from '@app/models/utility';
import { catchError, first, retry, timeout } from 'rxjs/operators';
import { MyGeolocation } from '@app/models/MyGeolocation';
import { Terminal } from '@app/models/terminal';
import { SwUpdate } from '@angular/service-worker';
import { AudioService } from '@app/services/audio.service';
import { StackLogService } from '@app/services/stack-log.service';
import { StampData } from '@app/models/StampData';
import { ObcApiService } from '@app/services/obc-api.service';
import { addDoc, collection, doc, DocumentData, DocumentReference, getDoc } from 'firebase/firestore';

declare var MUNI_ARRAY: { [key: string]: string; };

export interface GeoData {
  latitude: number;
  longitude: number;
  address: string;
}

@Component({
  selector: 'app-punch-stamp',
  templateUrl: './punch-stamp.component.html',
  styleUrls: ['./punch-stamp.component.css']
})
export class PunchStampComponent implements OnInit, OnDestroy {
  public appVersion: string = packageJson.version;

  now: Observable<Date>;
  intervalList = [];

  users: UserEx[];
  terminal: Terminal;

  statusStyle = 'normal';
  statusText = '';

  geoData: GeoData = { address: '', latitude: null, longitude: null };
  lastUpdateGeoDataTime: Date;

  scanning = false;
  ndef?: NDEFReader;
  lastPunchKey = '';
  lastPunchTime: Date;

  selectedKind = '0';
  kinds = ['出社', '退社', '外出', '戻り'];

  abortController: AbortController = null;

  readingEvent: EventListenerOrEventListenerObject = null;
  private readonly logCategory = 'punch';

  constructor(
    private auth: AuthService,
    private firestore: Firestore,
    private userService: UserService,
    private terminalService: TerminalService,
    private http: HttpClient,
    private analytics: Analytics,
    private audioService: AudioService,
    private swUpdate: SwUpdate,
    private stackLogService: StackLogService,
    private obcApiService: ObcApiService
  ) { }

  async ngOnInit() {
    setUserProperties(this.analytics, { 'dimension1': 'PWA端末' });
    setUserProperties(this.analytics, { 'dimension2': '打刻専用' });
    setUserProperties(this.analytics, { 'dimension3': this.appVersion });

    // ブラウザバック検知の為にセッション履歴のスタックに追加
    // この変更を検知してpopstateイベントが発火する
    history.pushState(null, null, location.href);
    // ユーザ操作イベント内でAudioContextを初期化する必要がある。
    const eventName = typeof document.ontouchend !== 'undefined' ? 'touchend' : 'mouseup';
    document.addEventListener(eventName, this.initAudioContext);

    this.statusText = '時刻同期確認中...';
    void this.stackLogService.log('時刻同期確認中...', { category: 'punch' });
    this.now = new Observable((observer) => {
      this.intervalList.push(setInterval(async () => {
        const date = new Date();

        const preUpdateDateStr: string = localStorage.getItem('preUpdateDate');

        let canUpdate = false;

        if (preUpdateDateStr === null) {
          // アップデートをしたことが無い場合 初回
          canUpdate = true;
        } else {
          const preUpdateDate = new Date(preUpdateDateStr);

          if (preUpdateDate.getDate() !== date.getDate()) {
            // 同日の場合はアップデートしない
            if (date.getHours() >= 5) {
              // 5時以降だった場合にアップデート
              canUpdate = true;
            }
          }
        }

        if (canUpdate) {
          // アップデート
          console.log('start update');
          void this.stackLogService.log('start update', { category: this.logCategory });
          if (this.swUpdate.isEnabled) {
            console.log('service worker enabled');
            void this.stackLogService.log('service worker enabled', { category: this.logCategory });
            this.swUpdate.activateUpdate().then(() => {
              console.log(`Update finish`);
              // 最終アップデート時間を更新
              localStorage.setItem('preUpdateDate', date.toString());
              void this.stackLogService.log('Update finish', { category: this.logCategory });
              window.location.reload();
            });
          }
        }

        if (this.lastUpdateGeoDataTime) {
          const diff = date.getTime() - this.lastUpdateGeoDataTime.getTime();
          this.lastUpdateGeoDataTime = date;
          if (diff / (60 * 1000) >= 30) {
            // 30分置きに位置情報更新
            this.getAddress().then(gData => {
              this.geoData = gData;
            });
          }
        }

        observer.next(date);
      }, 1000));
    });

    this.lastPunchTime = new Date();

    try {
      this.statusText = 'ユーザー情報取得中...';
      void this.stackLogService.log('ユーザー情報取得中...', { category: this.logCategory });
      this.users = await this.userService.getUsers();
      if (this.users.length === 0) {
        throw new Error('user does not exist');
      }
    } catch (error) {
      this.errorSound();
      this.statusText = 'ユーザー情報が取得できませんでした。';
      console.error(error);
      void this.stackLogService.error(error, { category: this.logCategory });
      this.statusStyle = 'error';
      return;
    }

    try {
      this.statusText = '端末情報取得中...';
      void this.stackLogService.log('端末情報取得中...', { category: this.logCategory });
      const serialId = localStorage.getItem('serialId');
      if (!serialId) {
        throw new Error('serialId does not exist');
      }
      this.terminal = await this.terminalService.getTerminal(serialId);
    } catch (error) {
      this.errorSound();
      this.statusText = '端末情報が取得できませんでした。';
      console.error(error);
      void this.stackLogService.error(error, { category: this.logCategory });
      this.statusStyle = 'error';
      return;
    }

    try {
      this.statusText = 'NFCリーダー起動中...';
      void this.stackLogService.log('NFCリーダー起動中...', { category: this.logCategory });
      await this.startNFCReader();
    } catch (error) {
      this.errorSound();
      this.statusText = 'NFCリーダーが有効ではありません。';
      console.error(error);
      void this.stackLogService.error(error, { category: this.logCategory });
      this.statusStyle = 'error';
      return;
    }

    this.geoData.address = '';
    this.geoData.latitude = null;
    this.geoData.longitude = null;

    this.getAddress().then(gData => {
      this.geoData = gData;
      this.lastUpdateGeoDataTime = new Date();
    });
  }

  async startNFCReader() {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (!this.scanning) {
          await this.startScan();
        }
      } catch (error) {
        reject(error);
        return;
      }
      this.scanning = true;
      this.statusText = '読み取り可能';
      this.statusStyle = 'normal';
      void this.stackLogService.log('読み取り可能', { category: this.logCategory });
      resolve();
    });
  }

  async startScan() {
    return new Promise<void>(async (resolve, reject) => {
      if (!('NDEFReader' in window)) {
        reject(new Error('NDEFReader is not exist'));
        return;
      }
      try {
        const status = await navigator.permissions.query({ name: 'nfc' as PermissionName });
        if (status.state === 'prompt') {
          void this.stackLogService.log('NFCリーダー有効化ダイアログ表示', { category: this.logCategory });
          if (confirm('NFCリーダーを有効にしてください。有効化しますか？')) {
            void this.stackLogService.log('NFCリーダー有効化プロセス開始', { category: this.logCategory });
            this.ndef = new NDEFReader();
            // リーダー有効化チェックの為に一度scan() エラーでハンドリング
            let abortController = new AbortController();
            await this.ndef.scan({ signal: abortController.signal });
            abortController.abort(); // すぐに中止
            abortController = null;
            void this.stackLogService.log('NFCリーダー有効化プロセス終了', { category: this.logCategory });
            window.location.reload();
          }
          throw new Error('NDEFReader is not valid');
        }
        if (status.state === 'denied') {
          alert('NFCリーダー権限がブロックされています。NFCリーダー権限を有効にしてください。');
          throw new Error('NDEFReader is not valid');
        }
        this.ndef = new NDEFReader();

        // リーダー有効化チェックの為に一度scan() エラーでハンドリング
        let ctlr = new AbortController();
        await this.ndef.scan({ signal: ctlr.signal });
        ctlr.abort(); // すぐに中止
        ctlr = null;

      } catch (err) {
        reject(err);
        return;
      }
      this.read().then(async ({ serialNumber }) => {
        this.scanning = false;
        const key = (serialNumber as string).split(':').join('');
        this.onSubmit(key);
      }).catch(err => {
        console.error(err);
        reject(err);
      });
      resolve();
    });
  }

  read() {
    return new Promise<any>(async (resolve, reject) => {
      try {
        if (this.abortController) {
          // すでにリーダーを起動していた場合は作り直す
          this.abortController.abort();
          this.abortController = null;
        }
        this.abortController = new AbortController();
        this.readingEvent = (event: Event) => {
          if (this.scanning) {
            resolve(event);
          }
        };
        this.ndef.scan({ signal: this.abortController.signal }).then(() => {
          this.ndef.addEventListener('reading', this.readingEvent, { once: true });
        }).catch(err => reject(err));
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  async logout() {
    await this.auth.logout();
  }

  ngOnDestroy() {
    if (this.intervalList) {
      this.intervalList.forEach((interval) => {
        clearInterval(interval);
      });
    }

    // 各イベント解約
    const eventName = typeof document.ontouchend !== 'undefined' ? 'touchend' : 'mouseup';
    document.removeEventListener(eventName, this.initAudioContext);

    // リーダーの停止
    if (this.abortController !== null) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.scanning = false;
    this.ndef?.removeEventListener('reading', this.readingEvent);
    this.ndef = null;
  }

  async onSubmit(key: string) {
    const kind: string = this.selectedKind;

    // 打刻種別確定後に鳴動
    this.acceptSound();
    this.statusText = '打刻開始';
    this.statusStyle = 'success';
    void this.stackLogService.log('打刻開始', { category: this.logCategory });

    const user = this.users.find(val => val.key === key);
    const date = await firstValueFrom(this.now.pipe(first()));

    const diff = (date.getTime() - this.lastPunchTime.getTime()) / 1000;
    if (this.lastPunchKey === key && diff <= 3) {
      this.errorSound();
      this.statusText = '連続打刻は出来ません。';
      this.statusStyle = 'error';
      void this.stackLogService.error(new Error('連続打刻は出来ません。'), { category: this.logCategory });

      // エラー時は2秒後に打刻可能
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.startNFCReader();
      return;
    }

    this.lastPunchTime = date;
    this.lastPunchKey = key;
    const stampData: StampData = {
      key: key,
      kind: Number(kind) + 10,
      placeId: this.terminal.id,
      timeStamp: Utility.convertDateToFullDateTime(date),
      latitude: this.geoData.latitude,
      longitude: this.geoData.longitude,
      address: this.geoData.address
    };

    const userLog = user
      ? {
        id: user.id,
        uid: user.uid,
        key: user.key,
        bugyoParallelOperation: user.bugyoParallelOperation
      }
      : null;
    const logPayload = {
      cardKey: key,
      selectedKind: kind,
      resolvedKind: stampData.kind,
      user: userLog,
      terminalId: stampData.placeId,
      timeStamp: stampData.timeStamp,
      latitude: stampData.latitude,
      longitude: stampData.longitude,
      address: stampData.address
    };
    void this.stackLogService.log('打刻リクエスト情報', {
      category: this.logCategory,
      context: logPayload
    });

    if (!window.navigator.onLine) {
      // オンライン確認
      this.errorSound();
      this.statusText = 'インターネット接続が確認できません';
      this.statusStyle = 'error';
      void this.stackLogService.error(new Error('インターネット接続が確認できません'), {
        category: this.logCategory
      });

      // エラー時は2秒後に打刻可能
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.startNFCReader();
      return;
    }

    // TODO 見つからない場合は正社員打刻(奉行打刻)とするため、完全移行時に削除
    if (!user) {
      this.errorSound();
      this.statusText = '登録されていないICカードです';
      this.statusStyle = 'error';
      void this.stackLogService.error(
        new Error(`登録されていないICカードです。(key:${key})`),
        { category: this.logCategory }
      );

      // エラー時は2秒後に打刻可能
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.startNFCReader();
      return;
    }

    // タスクキューに打刻タスクを追加
    setTimeout(async () => {
      // OBC打刻
      if (user.bugyoParallelOperation) {
        try {
          void this.punchStampToObc(stampData);// 非同期で奉行打刻実行
        } catch (error) {
          console.error('奉行打刻処理で例外発生', error);// 念のためキャッチ、握りつぶす
        }
      }

      try {
        // firestore打刻
        await this.punchStampToFirestore(stampData, user);
        this.finishSound();

        console.log(`打刻完了: ${stampData.key} ${stampData.timeStamp}`);
      } catch (error) {
        //エラーログ出力
        console.error(`打刻失敗: ${stampData.key} ${stampData.timeStamp}`, error);
        void this.stackLogService.error(
          new Error(`打刻失敗: ${stampData.key}} ${stampData.timeStamp}`),
          {
            category: this.logCategory,
            context: { userId: user?.id, stampData }
          }
        );

        // リーダーの停止
        if (this.abortController !== null) {
          this.abortController.abort();
          this.abortController = null;
        }

        // エラー通知
        this.errorSound();
        this.statusText = '打刻失敗';
        this.statusStyle = 'error';

        // エラー時は2秒後に打刻可能
        await new Promise(resolve => setTimeout(resolve, 2000));
        this.scanning = false;
        this.startNFCReader();
      }
    }, 0);

    this.statusText = '打刻完了';

    // 1秒後に打刻可能
    await new Promise(inResolve => setTimeout(inResolve, 1000));
    this.startNFCReader();
  }

  punchStampToObc(stampData: StampData) {
    return new Promise<void>(async (resolve, reject) => {
      console.log('奉行打刻開始');
      void this.stackLogService.log(`奉行打刻開始：'${stampData.timeStamp}`, { category: this.logCategory });

      try {
        await this.obcApiService.punchStamp(stampData);

        console.log('奉行打刻完了');
        void this.stackLogService.log(`奉行打刻完了：'${stampData.timeStamp}`, { category: this.logCategory });

        resolve();
      } catch (error) {
        const obcError = error instanceof Error ? error : new Error(String(error));
        console.error(`奉行打刻エラー: ${obcError.message}`, error);
        void this.stackLogService.error(
          obcError,
          {
            category: this.logCategory,
            context: { phase: 'obcPunch', stampData }
          }
        );

        reject(error);
      }
    });
  }

  punchStampToFirestore(stampData: StampData, user: UserEx) {
    return new Promise<void>(async (resolve, reject) => {
      let ref: DocumentReference<DocumentData> | null = null;
      void this.stackLogService.log(`Firestore打刻中：${stampData.timeStamp}`, {
        category: this.logCategory,
        context: { userId: user.id, stampData }
      });

      try {
        ref = await new Promise<DocumentReference<DocumentData>>((inResolve, inReject) => {
          const timeoutFunc = setTimeout(() => {
            const timeoutError = new Error('firestore add-operation timeout Error');
            void this.stackLogService.error(timeoutError, {
              category: this.logCategory,
              context: { userId: user.id, stampData }
            });
            inReject(timeoutError);
          }, 3000);

          addDoc(collection(this.firestore, `time-stamp/stamps/${user.uid}`), stampData)
            .then(docRef => {
              clearTimeout(timeoutFunc);
              inResolve(docRef);
            })
            .catch(err => {
              const error = new Error(`firestore add-operation Error \n${err}`);
              void this.stackLogService.error(error, {
                category: this.logCategory,
                context: { userId: user.id, stampData }
              });
              inReject(error);
            });
        });

        if (!ref) {
          const nullRefError = new Error('DocumentReference is null');
          void this.stackLogService.error(nullRefError, {
            category: this.logCategory,
            context: { userId: user.id, stampData }
          });
          throw nullRefError;
        }
      } catch (error) {
        console.error(error);
        void this.stackLogService.error(new Error(`Firestore打刻に失敗しました：${stampData.timeStamp}`), {
          category: this.logCategory,
          context: { userId: user.id, stampData }
        });

        reject(error);
        return;
      }

      // 書き込みチェック
      try {
        const snapshot = await getDoc(doc(this.firestore, `time-stamp/stamps/${user.uid}/${ref.id}`));
        const targetData = Object.assign({}, snapshot.data() as StampData);
        if (this.isStampDataEqual(stampData, targetData)) {
          void this.stackLogService.log('打刻完了', {
            category: this.logCategory,
            context: { userId: user.id, stampData }
          });
        } else {
          throw new Error('Consistency check failed');
        }
      } catch (error) {
        console.error(error);
        void this.stackLogService.error(new Error(`打刻に失敗しました:${stampData.timeStamp}:${error}`), {
          category: this.logCategory,
          context: { userId: user.id, stampData }
        });
        reject(error);
        return;
      }

      void this.stackLogService.log(`Firestore打刻完了：'${stampData.timeStamp}`, { category: this.logCategory });
      resolve();
    });
  }
  getAddress(): Promise<GeoData> {
    return new Promise<GeoData>(async (resolve, reject) => {
      const gData: GeoData = { address: '', latitude: null, longitude: null };

      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'prompt') {
        if (confirm('位置情報を有効にしてください。有効化しますか？')) {
          if (navigator.geolocation) {
            await MyGeolocation.getCurrentPosition();
            window.location.reload();
          }
        }
        resolve(gData);
      }
      if (status.state === 'denied') {
        alert('位置情報権限がブロックされています。位置情報権限を有効にしてください。');
        void this.stackLogService.error(
          new Error('位置情報権限がブロックされています。位置情報権限を有効にしてください。'),
          { category: this.logCategory, context: { feature: 'geolocation' } }
        );
        resolve(gData);
      }
      // Geolocation APIに対応している
      if (navigator.geolocation) {
        // 現在位置を取得できる場合の処理
        // 現在位置を取得する
        const position = await MyGeolocation.getCurrentPosition();
        const data = position.coords;
        gData.latitude = data.latitude;
        gData.longitude = data.longitude;

        let url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${gData.latitude}&longitude=${gData.longitude}&localityLanguage=ja`;
        console.log(url);
        void this.stackLogService.log('Reverse geocode request (global)', {
          category: this.logCategory,
          context: { url }
        });

        let response: HttpResponse<any>;
        try {
          const res = await new Promise<HttpResponse<any>>((inResolve, inReject) => {
            this.http.get(url)
              .pipe(catchError((err: HttpErrorResponse) => {
                inReject(new Error(`${err.status}: ${err.message}`));
                throw new Error(err.message);
              }))
              .subscribe((httpResponse: HttpResponse<any>) => {
                inResolve(httpResponse);
              });
          });
          response = res as HttpResponse<any>;
        } catch (error) {
          console.error(error);
          console.error('continentCode取得エラー');
          void this.stackLogService.error(error, {
            category: this.logCategory,
            context: { feature: 'reverseGeocode-global', url }
          });
          resolve(gData);
        }

        if (response !== undefined) {
          if (response['countryCode'] === 'JP') {
            url = `https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress?lat=${gData.latitude}&lon=${gData.longitude}`;
            let jpAddressResponse: HttpResponse<any>;
            try {
              const res = await new Promise<HttpResponse<any>>((inResolve, inReject) => {
                this.http.get(url)
                  .pipe(
                    timeout(3000),
                    retry(3),
                    catchError((err: HttpErrorResponse) => {
                      inReject(new Error(`${err.status}: ${err.message}`));
                      throw new Error(err.message);
                    }))
                  .subscribe((httpResponse: HttpResponse<any>) => {
                    inResolve(httpResponse);
                  });
              });
              jpAddressResponse = res;
            } catch (error) {
              console.error(error);
              gData.address = '';
              console.error('現在地詳細取得エラー');
              void this.stackLogService.error(error, {
                category: this.logCategory,
                context: { feature: 'reverseGeocode-jp', url }
              });
              resolve(gData);
            }
            if (jpAddressResponse !== undefined) {
              try {
                const municipalResult = MUNI_ARRAY[jpAddressResponse['results']['muniCd']].split(',');
                gData.address = [municipalResult[1], municipalResult[3], jpAddressResponse['results']['lv01Nm']].join(' ');
              } catch (error) {
                console.error(error);
                gData.address = '';
                console.error('現在地詳細取得エラー');
                void this.stackLogService.error(error, {
                  category: this.logCategory,
                  context: { feature: 'reverseGeocode-jp-parse', url }
                });
                resolve(gData);
              }
            }
          } else {
            // 日本以外の場合
            gData.address = [response['countryName'], response['principalSubdivision'], response['locality']].join(' ');
          }
          console.log(gData.address);
          void this.stackLogService.log('Resolved address', {
            category: this.logCategory,
            context: { address: gData.address, latitude: gData.latitude, longitude: gData.longitude }
          });
          resolve(gData);
        }

      } else { // Geolocation APIに対応していない
        // 現在位置を取得できない場合の処理
        alert('この端末では、現在位置を取得できません。');
        void this.stackLogService.error(new Error('この端末では、現在位置を取得できません。'), {
          category: this.logCategory,
          context: { feature: 'geolocation' }
        });
        resolve(gData);
      }
    });
  }

  // html呼び出し用
  beep(frequency: number, duration: number = 50) {
    this.audioService.beep(frequency, duration);
  }

  beepRepeat(frequency: number, onDuration: number, offDuration: number, count: number) {
    this.audioService.beepRepeat(frequency, onDuration, offDuration, count);
  }

  // 各鳴動パターン

  // 打刻受付
  async acceptSound() {
    await this.audioService.beep(2400, 100);
  }

  // エラー
  async errorSound() {
    await this.audioService.beepRepeat(2800, 100, 50, 3);
  }

  // 終了音
  async finishSound() {
    await this.audioService.beep(2400, 500);
  }

  isStampDataEqual(data1: StampData, data2: StampData) {
    const keys1 = Object.keys(data1);
    const keys2 = Object.keys(data2);

    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      if (data1[key] !== data2[key]) {
        return false;
      }
    }

    return true;
  }

  initAudioContext() {
    const eventName = typeof document.ontouchend !== 'undefined' ? 'touchend' : 'mouseup';
    // 初回のみで良いためリスナーを解除
    document.removeEventListener(eventName, this.initAudioContext);
    this.audioService?.init();
  }

  @HostListener('window:beforeunload', ['$event'])
  async unloadHandler(_event: Event) {
    // ページを離れる際には必ずログアウトを行う
    // GuardのcanDeactivateではURL直接指定のページ遷移などに対応出来ないためbeforeunloadイベントで対応する
    await this.auth.logoutWithoutNavigate();
  }

  @HostListener('window:popstate', ['$event'])
  async browserBackHandler(_event: Event) {
    // 「戻る」ボタン押下を検知する。
    console.log('this BrowserBack operation change to reload operation');

    // ブラウザバックの打ち消し
    history.go(1);
  }

  @HostListener('window:onerror', ['$message', '$source', '$lineno', '$colno', '$error'])
  async OnError(message: string, source: string, lineno: number, colno: number, error: Error) {
    // グローバルエラーの検知
    void this.stackLogService.log('Global Error detected', {
      category: this.logCategory,
      context: { message, source, lineno, colno }
    });
    void this.stackLogService.error(error ?? new Error(message), {
      category: this.logCategory,
      context: { source, lineno, colno }
    });
  }

}
